// 🖼️ معاينة الملفات/الصور المختارة للرفع - تُستخدم بكل مكان بالنظام بيه رفع
// صور أو ملفات، حتى المستخدم يشوف شنو اختار قبل الإرسال ويكدر يبدّل أو يحذف.
//
// files: مصفوفة عناصرها File (ملف مختار من الجهاز) أو رابط نصي (ملف مرفوع
// سابقاً). onChange: يستلم المصفوفة الجديدة بعد الحذف/التبديل - نفس الـsetter
// مال الـstate (مثلاً setCompleteImages).
//
// الاستخدام:
//   <UploadPreview files={images} onChange={setImages} />
import React, { useEffect, useMemo, useRef } from "react";

const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif|avif)(\?|$)/i;

const VIDEO_EXT_RE = /\.(mp4|mov|webm|m4v|avi|mkv)(\?|$)/i;

const isFile = (item) => typeof File !== "undefined" && item instanceof File;

const getName = (item) => {
  if (isFile(item)) return item.name;
  if (item && typeof item === "object") return item.name || item.fileName || "ملف";
  try {
    // روابط Firebase Storage: الاسم بعد آخر %2F
    const path = decodeURIComponent(new URL(item).pathname);
    const last = path.split("/").pop() || "ملف";
    return last.replace(/^\d{10,}-/, ""); // نشيل بادئة التوقيت
  } catch {
    return String(item || "ملف");
  }
};

const getUrl = (item) =>
  item && typeof item === "object" && !isFile(item) ? item.url : item;

const isImageItem = (item) => {
  if (isFile(item)) return item.type.startsWith("image/");
  const url = getUrl(item);
  return typeof url === "string" && (IMAGE_EXT_RE.test(url) || /^data:image/.test(url));
};

const isVideoItem = (item) => {
  if (isFile(item)) return item.type.startsWith("video/");
  const url = getUrl(item);
  return typeof url === "string" && VIDEO_EXT_RE.test(url);
};

const styles = {
  wrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
    marginBottom: 4,
  },
  tile: {
    position: "relative",
    width: 92,
    border: "1px solid #ddd",
    borderRadius: 8,
    background: "#fafafa",
    overflow: "hidden",
    fontFamily: "inherit",
  },
  thumb: {
    width: "100%",
    height: 72,
    objectFit: "cover",
    display: "block",
    cursor: "zoom-in",
  },
  fileIcon: {
    height: 72,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 30,
    textDecoration: "none",
  },
  name: {
    fontSize: 10,
    padding: "2px 4px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    direction: "ltr",
    textAlign: "center",
  },
  actions: {
    display: "flex",
    borderTop: "1px solid #eee",
  },
  actionBtn: {
    flex: 1,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: 11,
    padding: "3px 0",
    fontFamily: "inherit",
  },
};

export default function UploadPreview({ files, onChange, disabled = false }) {
  const list = useMemo(() => (Array.isArray(files) ? files : []), [files]);
  const replaceInputRef = useRef(null);
  const replaceIndexRef = useRef(-1);

  // روابط معاينة مؤقتة للملفات المحلية (تنمسح من الذاكرة لما تتغير القائمة)
  const previewUrls = useMemo(
    () => list.map((item) => (isFile(item) ? URL.createObjectURL(item) : getUrl(item))),
    [list]
  );
  useEffect(
    () => () => {
      list.forEach((item, i) => {
        if (isFile(item)) URL.revokeObjectURL(previewUrls[i]);
      });
    },
    [list, previewUrls]
  );

  if (!list.length) return null;

  const removeAt = (index) => onChange(list.filter((_, i) => i !== index));

  const startReplace = (index) => {
    replaceIndexRef.current = index;
    if (replaceInputRef.current) {
      // نفس نوع الملف الأصلي (صورة تتبدل بصورة)
      replaceInputRef.current.accept = isImageItem(list[index])
        ? "image/*"
        : isVideoItem(list[index])
        ? "video/*"
        : "";
      replaceInputRef.current.click();
    }
  };

  const handleReplacePicked = (e) => {
    const picked = e.target.files?.[0];
    e.target.value = "";
    const index = replaceIndexRef.current;
    if (!picked || index < 0) return;
    onChange(list.map((item, i) => (i === index ? picked : item)));
  };

  return (
    <div style={styles.wrap}>
      {list.map((item, i) => {
        const url = previewUrls[i];
        const name = getName(item);
        return (
          <div key={`${name}-${i}`} style={styles.tile} title={name}>
            {isImageItem(item) ? (
              <img src={url} alt={name} style={styles.thumb} />
            ) : isVideoItem(item) ? (
              <a href={url} target="_blank" rel="noopener noreferrer">
                <video src={url} muted preload="metadata" style={{ ...styles.thumb, background: "#000" }} />
              </a>
            ) : (
              <a href={url} target="_blank" rel="noopener noreferrer" style={styles.fileIcon}>
                📄
              </a>
            )}
            <div style={styles.name}>{name}</div>
            {!disabled && (
              <div style={styles.actions}>
                <button
                  type="button"
                  style={{ ...styles.actionBtn, color: "#1976d2" }}
                  onClick={() => startReplace(i)}
                >
                  تبديل
                </button>
                <button
                  type="button"
                  style={{ ...styles.actionBtn, color: "#d32f2f" }}
                  onClick={() => removeAt(i)}
                >
                  حذف
                </button>
              </div>
            )}
          </div>
        );
      })}
      <input ref={replaceInputRef} type="file" hidden onChange={handleReplacePicked} />
    </div>
  );
}

// يضيف الملفات المختارة للقائمة الموجودة (بدل ما يستبدلها) ويصفّر الـinput
// حتى يكدر يختار نفس الملف مرة ثانية بعد حذفه
export const appendPickedFiles = (e, setter) => {
  const picked = Array.from(e.target.files || []);
  e.target.value = "";
  if (picked.length) setter((prev) => [...(prev || []), ...picked]);
};
